import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { FaUser } from 'react-icons/fa';
import { IoMicOffSharp } from 'react-icons/io5';
import { useAuthContext } from '../../contexts/AuthProvider';
import { useNavbarContext } from '../../contexts/NavbarProvider';
import { useSessionContext } from '../../contexts/SessionProvider';
import { ParticipantData } from '../../types/session';
import { showFullName, showPicture } from '../../utils/commonFunctions';

const RemoteVideoStream = ({ remoteUser }: { remoteUser: ParticipantData }) => {
  // integration of react hooks here
  const [isRemoteScreenStreamCaptured, setIsRemoteScreenStreamCaptured] =
    useState<boolean>(false);

  // integration of context hooks here
  const {
    mutedStatus,
    remoteStream,
    remoteStreamRef,
    remoteScreenStream,
    remoteScreenStreamRef,
  } = useSessionContext();
  const { currentUserType } = useAuthContext();

  const { isMobile } = useNavbarContext();

  // setting the remote streams in remote video ref here
  useEffect(() => {
    const video = remoteStreamRef.current;

    if (!video || !remoteStream) return;

    video.srcObject = remoteStream;
    video.play().catch((e) => console.warn('Remote video blocked:', e));

    const onLoaded = () => {
      const isPortrait = video.videoHeight > video.videoWidth;

      video.style.objectFit = isPortrait ? 'contain' : 'cover';
    };

    video.addEventListener('loadedmetadata', onLoaded);

    return () => video.removeEventListener('loadedmetadata', onLoaded);
  }, [remoteStream, remoteStreamRef]);

  // setting the remote screen streams in remote video ref here
  useEffect(() => {
    const screenVideo = remoteScreenStreamRef.current;

    if (!screenVideo || !remoteScreenStream) return;

    screenVideo.srcObject = remoteScreenStream;
    screenVideo.play().catch((e) => console.warn('Remote video blocked:', e));

    const onLoaded = () => {
      const isPortrait = screenVideo.videoHeight > screenVideo.videoWidth;

      screenVideo.style.objectFit = isPortrait ? 'contain' : 'cover';
    };

    screenVideo.addEventListener('loadedmetadata', onLoaded);

    return () => screenVideo.removeEventListener('loadedmetadata', onLoaded);
  }, [remoteScreenStream, remoteScreenStreamRef]);

  // playing user joining audio sound here
  useEffect(() => {
    if (remoteStream) {
      const joinedSessionAudio = new Audio('/assets/audio/session-join.wav');

      joinedSessionAudio.play();
    }
  }, [remoteStream]);

  // playing user screen share on/off audio sound here
  useEffect(() => {
    if (remoteScreenStream) {
      setIsRemoteScreenStreamCaptured(true);

      const screenShareAudio = new Audio('/assets/audio/screen-share.wav');

      screenShareAudio.play();
    }

    if (isRemoteScreenStreamCaptured && remoteScreenStream === null) {
      setIsRemoteScreenStreamCaptured(false);

      const screenShareOffAudio = new Audio(
        '/assets/audio/screen-share-off.wav'
      );

      screenShareOffAudio.play();
    }
  }, [remoteScreenStream, isRemoteScreenStreamCaptured]);

  console.log('REMOTE USER: ', remoteUser, remoteStream?.getAudioTracks());
  console.log('REMOTE SCREEN: ', remoteScreenStream);

  // rendering remote video stream component here
  return (
    <>
      {remoteScreenStream ? (
        <div className='size-full rounded-lg overflow-hidden'>
          <video
            ref={remoteScreenStreamRef}
            // className='absolute top-0 left-0 min-w-full min-h-full xl:object-fill'
            className='size-full object-cover object-center'
            autoPlay
          />
        </div>
      ) : null}

      {remoteStream ? (
        <motion.div
          initial={{ height: '100%', width: '100%' }}
          animate={
            remoteScreenStream
              ? isMobile
                ? {
                    height: 100,
                    width: 200,
                    right: 10,
                    top: 100,
                    borderRadius: '8px',
                  }
                : {
                    height: 120,
                    width: 240,
                    right: 20,
                    top: 20,
                    borderRadius: '8px',
                  }
              : {
                  height: '100%',
                  width: '100%',
                  right: 0,
                  top: 0,
                  borderRadius: 0,
                }
          }
          className='absolute overflow-hidden'
        >
          <div className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 lg:translate-y-0 lg:top-0 size-full rounded-lg overflow-hidden'>
            <video
              ref={remoteStreamRef}
              className='size-full object-cover object-center'
              autoPlay
            />

            {remoteUser?.associate_first_name ||
            remoteUser?.client_first_name ? (
              <div className='absolute left-2 bottom-2 space-y-2 z-10'>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={
                    currentUserType === 'client'
                      ? mutedStatus.is_associate_audio_muted
                        ? { opacity: 1 }
                        : { opacity: 0 }
                      : mutedStatus.is_client_audio_muted
                      ? { opacity: 1 }
                      : { opacity: 0 }
                  }
                >
                  <IoMicOffSharp
                    className={`text-base-red ${
                      remoteScreenStream ? 'size-5' : 'size-5 md:size-7'
                    }`}
                  />
                </motion.div>

                <div className='bg-base-black/50 text-base-white font-medium text-sm px-2 rounded-md'>
                  {remoteUser.role_name ? (
                    <p>
                      {showFullName(
                        remoteUser.associate_first_name as string,
                        remoteUser?.associate_middle_name as string,
                        remoteUser?.associate_last_name as string
                      )}
                    </p>
                  ) : (
                    <p>
                      {showFullName(
                        remoteUser.client_first_name,
                        remoteUser?.client_middle_name as string,
                        remoteUser?.client_last_name as string
                      )}
                    </p>
                  )}
                </div>
              </div>
            ) : null}
          </div>

          <motion.div
            initial={{ opacity: 0, display: 'none' }}
            animate={
              !remoteStream.getVideoTracks().length
                ? { opacity: 1, display: 'flex' }
                : currentUserType === 'client'
                ? mutedStatus.is_associate_video_muted
                  ? { opacity: 1, display: 'flex' }
                  : { opacity: 0, transitionEnd: { display: 'none' } }
                : mutedStatus.is_client_video_muted
                ? { opacity: 1, display: 'flex' }
                : { opacity: 0, transitionEnd: { display: 'none' } }
            }
            className='absolute top-1/2 -translate-y-1/2 lg:translate-y-0 lg:top-0 left-0 w-full h-1/3 md:h-1/2 lg:size-full items-center justify-center bg-base-black'
          >
            {remoteUser?.associate_profile_picture ? (
              <div
                className={`${
                  remoteScreenStream ? 'size-16' : 'size-32'
                } rounded-full overflow-hidden`}
              >
                <img
                  className='size-full object-cover'
                  src={showPicture(remoteUser.associate_profile_picture)}
                  alt={`avatar of ${showFullName(
                    remoteUser.associate_first_name as string,
                    remoteUser.associate_middle_name,
                    remoteUser.associate_last_name as string
                  )}`}
                />
              </div>
            ) : remoteUser?.client_profile_picture ? (
              <div
                className={`${
                  remoteScreenStream ? 'size-16' : 'size-32'
                } rounded-full overflow-hidden`}
              >
                <img
                  className='size-full object-cover'
                  src={showPicture(remoteUser.client_profile_picture)}
                  alt={`avatar of ${showFullName(
                    remoteUser.associate_first_name as string,
                    remoteUser.associate_middle_name,
                    remoteUser.associate_last_name as string
                  )}`}
                />
              </div>
            ) : (
              <div
                className={`bg-base-light-gray/60 rounded-full flex items-center justify-center ${
                  remoteScreenStream ? 'size-16' : 'size-32'
                }`}
              >
                <FaUser
                  className={`${
                    remoteScreenStream ? 'h-8 w-8' : 'h-20 w-20'
                  } text-base-black`}
                />
              </div>
            )}

            {remoteUser?.associate_first_name ||
            remoteUser?.client_first_name ? (
              <div className='absolute left-2 bottom-2 space-y-2 z-10'>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={
                    currentUserType === 'client'
                      ? mutedStatus.is_associate_audio_muted
                        ? { opacity: 1 }
                        : { opacity: 0 }
                      : mutedStatus.is_client_audio_muted
                      ? { opacity: 1 }
                      : { opacity: 0 }
                  }
                >
                  <IoMicOffSharp
                    className={`text-base-red ${
                      remoteScreenStream ? 'size-5' : 'size-5 md:size-7'
                    }`}
                  />
                </motion.div>

                <div className='bg-base-black/50 text-base-white font-medium text-sm px-2 rounded-md'>
                  {remoteUser.role_name ? (
                    <p>
                      {showFullName(
                        remoteUser.associate_first_name as string,
                        remoteUser?.associate_middle_name as string,
                        remoteUser?.associate_last_name as string
                      )}
                    </p>
                  ) : (
                    <p>
                      {showFullName(
                        remoteUser.client_first_name,
                        remoteUser?.client_middle_name as string,
                        remoteUser?.client_last_name as string
                      )}
                    </p>
                  )}
                </div>
              </div>
            ) : null}
          </motion.div>
        </motion.div>
      ) : null}
    </>
  );
};

export default RemoteVideoStream;
